import { MagnifyingGlassIcon } from '@heroicons/react/20/solid';
import { useEffect, useMemo, useRef, useState } from 'react';

import FilterSegmentShell from './FilterSegmentShell';

// One segmented multi-select of the approved filter bars — a label + chevron
// cell that opens its OWN scoped option list (GRAVY-10305 for Calendar,
// GRAVY-10315 for the three action queues).
//
// This is the unit the design is built from. The control it replaces put every
// dimension's options into a single vertical checkbox list, so a tenant with
// many clients produced a scrolling menu that covered the page and there was no
// way to look at just one dimension.
//
// The trigger, menu chrome and dismissal rules live in FilterSegmentShell,
// which the Date range control shares; this file is the checkbox body and the
// staging rule.
//
// ## Search (GRAVY-10850)
//
// `searchable` adds the prototype's search row above the list
// (`prototypes/dashboard-filter`, `.dd-search-row`: magnifier + borderless
// input over a rule). It is opt-in rather than automatic because only the long
// lists want it — a three-option Status control with a search box reads as
// clutter. It filters the RENDERED list only: a staged selection that the query
// hides is still applied, so typing cannot silently drop a choice already made.
//
// The field sits inside the shell's `role="listbox"` menu, which is not strictly
// valid ARIA — but neither are the Apply/Clear buttons that have always been in
// there, and every spec driving this control reads the menu by that role.
// Tightening it means moving the role onto the options list and rewriting those
// queries: a real cleanup, and not this ticket's.
//
// ## Staged vs applied
//
// Selections are STAGED here and only leave on Apply (or Clear), because the
// old popover applied on every checkbox change — one request per click while
// the admin was still choosing. `staged` is seeded from `value` each time the
// menu opens, so dismissing without applying discards the edit and leaves the
// results exactly as they were.

export type SegmentOption = { value: string; label: string };

type Props = {
  /** Placeholder shown when nothing is selected, e.g. "Client", "All status". */
  label: string;
  options: SegmentOption[];
  /** Applied selection. */
  value: string[];
  onChange: (next: string[]) => void;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  align?: 'left' | 'right';
  /** Adds the search row above the options — for lists long enough to need it. */
  searchable?: boolean;
};

const FilterSegment = ({
  label,
  options,
  value,
  onChange,
  open,
  onOpenChange,
  align = 'left',
  searchable = false,
}: Props) => {
  const [staged, setStaged] = useState<string[]>(value);
  const [query, setQuery] = useState('');
  const searchRef = useRef<HTMLInputElement>(null);

  // Re-seed on open, and whenever the applied value changes underneath (Reset
  // filters clears every segment at once).
  useEffect(() => {
    if (!open) setStaged(value);
  }, [open, value]);

  // A query left over from last time would open the menu already filtered, with
  // the reason two clicks out of sight.
  useEffect(() => {
    if (!open) setQuery('');
  }, [open]);

  // Typing is the point of the control, so the caret starts in it. Focus is set
  // after the menu mounts; the shell hands it back to the trigger on Escape.
  useEffect(() => {
    if (open && searchable) searchRef.current?.focus();
  }, [open, searchable]);

  // Display only. `staged` is untouched by the query — see the header note.
  const visible = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return options;
    return options.filter((o) => o.label.toLowerCase().includes(q));
  }, [options, query]);

  const toggleStaged = (v: string) =>
    setStaged((prev) =>
      prev.includes(v) ? prev.filter((x) => x !== v) : [...prev, v],
    );

  const apply = () => {
    onChange(staged);
    onOpenChange(false);
  };

  const clear = () => {
    // Clearing this control applies immediately and leaves every OTHER control
    // untouched — the parent only ever receives this dimension's value.
    onChange([]);
    setStaged([]);
    onOpenChange(false);
  };

  // The trigger reports the selection, not the placeholder, once anything is
  // applied: one selection shows its label, several show a count.
  const selectedLabels = options
    .filter((o) => value.includes(o.value))
    .map((o) => o.label);
  const triggerText =
    selectedLabels.length === 0
      ? label
      : selectedLabels.length === 1
        ? selectedLabels[0]
        : `${label} (${selectedLabels.length})`;

  return (
    <FilterSegmentShell
      label={label}
      triggerText={triggerText}
      active={value.length > 0}
      disabled={options.length === 0}
      open={open}
      onOpenChange={onOpenChange}
      align={align}
    >
      {() => (
        <>
          {searchable && (
            <div className="dashv2-fseg-search">
              <MagnifyingGlassIcon aria-hidden="true" />
              <input
                ref={searchRef}
                type="text"
                value={query}
                placeholder={`Search ${label.toLowerCase()}`}
                aria-label={`Search ${label.toLowerCase()}`}
                data-testid={`dashv2-fseg-search-${label.toLowerCase().replace(/\s+/g, '-')}`}
                onChange={(e) => setQuery(e.target.value)}
              />
            </div>
          )}
          <div className="dashv2-fseg-options">
            {visible.map((o) => (
              <label className="dashv2-fseg-option" key={o.value}>
                <input
                  type="checkbox"
                  checked={staged.includes(o.value)}
                  onChange={() => toggleStaged(o.value)}
                />
                <span>{o.label}</span>
              </label>
            ))}
            {/* An empty list with no message reads as a broken dropdown. */}
            {visible.length === 0 && (
              <p className="dashv2-fseg-empty" data-testid="dashv2-fseg-empty">
                No matches
              </p>
            )}
          </div>
          <div className="dashv2-fseg-foot">
            <button
              type="button"
              className="dashv2-fseg-clear"
              disabled={value.length === 0 && staged.length === 0}
              onClick={clear}
            >
              Clear
            </button>
            <button type="button" className="dashv2-fseg-apply" onClick={apply}>
              Apply
            </button>
          </div>
        </>
      )}
    </FilterSegmentShell>
  );
};

export default FilterSegment;
